/* eslint-disable @next/next/no-img-element */
import React, { useState, useRef, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { StackedCarousel, ResponsiveContainer } from "react-stacked-center-carousel";
import { useWindowResize } from "utils/hooks";
import { ArrowBack, ArrowForward } from "@material-ui/icons";
import { Fab } from "@material-ui/core";
import { Carousel } from "../types/mockInterviewTypes";
import { trackMockInterviewEvent } from "../utils/trackers";
import useIsWebView from "modules/apnaAdvantage/utils/hooks/useIsWebView";

interface MIHeroCarousalProps {
	carousels: Carousel[];
}

interface CardProps {
	data: Carousel[];
	dataIndex: number;
}

/**
 * Renders the common card content used in all carousel types
 * @param title - Title text to display
 * @param companyLogo - Company logo URL
 * @param companyName - Company name for alt text
 * @param uiInfo - UI information for animations and person image
 * @returns Object containing backgroundStyle, headerContent and footerContent
 */
const renderCardContent = (
	title: string,
	companyLogo: string,
	companyName: string,
	uiInfo?: {
		bg_card_animation: string;
		bg_mobile_animation: string;
		bg_web_animation: string;
		person_image_url: string;
	}
) => {
	// Use background animation if available, otherwise use default background
	const backgroundStyle = uiInfo?.bg_card_animation
		? {
				backgroundImage: `url(${uiInfo.bg_card_animation})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
				backgroundColor: "#460299",
		  }
		: {
				backgroundColor: "#460299",
		  };

	return {
		backgroundStyle,
		headerContent: (
			<div className="flex flex-col items-center p-[16px]">
				<h1 className="m-0 text-center text-[14px] font-semibold leading-[20px] text-white">
					{title}
				</h1>
				<div className="relative z-10 mt-[2px]">
					<Image
						src={companyLogo}
						alt={companyName || title}
						width={70}
						height={27}
						priority
						className="object-contain"
					/>
				</div>
			</div>
		),
		footerContent: (
			<div className="flex h-[74px] w-full flex-col items-center rounded-b-[18px] bg-gradient-to-t from-black to-transparent py-[20px]">
				<div className="relative h-[120px] w-[162px] bg-gradient-to-r from-[#0FFFBB] to-[#7905FF] px-[8px] py-[4px] text-center text-[12px] font-semibold leading-[16px] text-white">
					Practice Interview
					{uiInfo?.person_image_url && (
						<div className="absolute right-0 top-0 h-[120px] w-[162px]">
							<Image
								src={uiInfo.person_image_url}
								alt="Interviewer"
								width={162}
								height={120}
								priority
								className="object-cover"
							/>
						</div>
					)}
				</div>
				<p className="mt-[4px] text-center text-[10px] font-normal leading-[12px] text-white">
					10 min AI Interview
				</p>
			</div>
		),
	};
};

/**
 * Card component used by the StackedCarousel
 * Memoized for performance
 */
export const Card = React.memo<CardProps>(({ data, dataIndex }) => {
	const item = data?.[dataIndex];
	const [isMobileView] = useWindowResize();
	const isWebView = useIsWebView();
	// Early validation check but not returning early
	let title = "";
	let slugId = "";
	let companyLogo = "";
	let companyName = "";
	let uiInfo;

	if (item) {
		({
			title = "",
			slug_id: slugId = "",
			company_logo: companyLogo = "",
			company_name: companyName = "",
			ui_info: uiInfo,
		} = item as Partial<Carousel>);
	}

	// Get backgroundStyle but ignore headerContent and footerContent to avoid lint errors
	const { backgroundStyle } = renderCardContent(
		title,
		companyLogo,
		companyName,
		uiInfo
	);

	// Track card click
	const handleCardClick = () => {
		trackMockInterviewEvent("Job prep card clicked", {
			job_role: title || "",
			company_name: companyName || "",
			slug_id: slugId || "",
			source: "Carousel",
			platform: isWebView ? "app" : "web",
		});
	};

	if (!item) return null;

	return (
		<Link href={`/interview-prep/mock-interview/${slugId}`}>
			<div
				role="button"
				tabIndex={0}
				className="relative flex h-[255px] w-[216px] flex-col justify-between overflow-hidden rounded-[21px]
					 border-[4px] border-[#F4F2F6] transition-all duration-300 hover:scale-[1.02] hover:shadow-lg md:h-[494px] md:w-[418px]"
				style={{
					userSelect: "none",
					boxShadow: "0px 1.06px 10.47px 7.41px rgba(0, 0, 0, 0.05)",
					...backgroundStyle,
				}}
				onClick={handleCardClick}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						handleCardClick();
					}
				}}
				aria-label={`Practice interview for ${title} at ${companyName}`}
			>
				{/* Gradient overlay */}
				<div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/70 via-transparent to-black/70" />

				{/* Header Content */}
				<div className="relative z-[2] flex flex-col items-center p-[16px] md:p-[32px]">
					<h1 className="m-0 text-center text-[14px] font-semibold leading-[20px] text-white md:text-[28px] md:leading-[38px]">
						{title}
					</h1>

					<div className="relative z-10 flex items-center space-x-[8px] md:mt-0">
						<Image
							src={companyLogo}
							alt={companyName || title}
							width={isMobileView ? 27 : 50}
							height={isMobileView ? 27 : 50}
							priority
							className="h-[27px] w-[27px] object-contain md:h-[50px] md:w-[50px]"
						/>
						<h1 className="m-0 text-center text-[14px] font-semibold leading-[20px] text-white md:text-[28px] md:leading-[38px]">
							{companyName}
						</h1>
					</div>
				</div>

				{/* Footer Content */}
				<div className="relative z-[2] flex w-full flex-col items-center rounded-b-[18px] bg-gradient-to-t from-black to-transparent py-[20px] md:py-[32px]">
					<div className="rounded-full bg-[linear-gradient(93.17deg,_#0066FF_4.67%,_#8F00FF_73.02%)] px-[16px] py-[8px] text-center text-[12px] font-semibold leading-[16px] text-white transition-all duration-300 hover:shadow-lg md:px-[24px] md:py-[12px] md:text-[18px] md:leading-[24px]">
						Practice Interview
					</div>
					<p className="m-0 mt-[8px] text-center text-[10px] font-normal leading-[12px] text-white md:mt-[8px] md:text-[16px] md:leading-[20px]">
						10 min AI Interview
					</p>
				</div>

				{/* Person Image */}
				{uiInfo?.person_image_url && (
					<div className="absolute bottom-0 left-1/2 z-[1] h-[160px] w-[120px] -translate-x-1/2 overflow-hidden md:h-[320px] md:w-[240px]">
						<Image
							src={uiInfo.person_image_url}
							alt="Interviewer"
							width={isMobileView ? 120 : 240}
							height={isMobileView ? 160 : 320}
							priority
							className="object-cover"
						/>
					</div>
				)}
			</div>
		</Link>
	);
});

Card.displayName = "Card";

/**
 * NavButton component for carousel navigation
 */
interface NavButtonProps {
	direction: "prev" | "next";
	onClick: () => void;
	ariaLabel: string;
}

const NavButton = ({ direction, onClick, ariaLabel }: NavButtonProps) => (
	<button
		type="button"
		onClick={onClick}
		className={`absolute ${
			direction === "prev" ? "left-[-20px]" : "right-[-20px]"
		} top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md focus:outline-none`}
		aria-label={ariaLabel}
	>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			className="h-4 w-4"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d={direction === "prev" ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
			/>
		</svg>
	</button>
);

/**
 * Simple carousel for 1-2 cards
 * Uses a basic navigation system with prev/next buttons
 */
const SimpleCarousel = ({ data = [] }: { data: Carousel[] }) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const isWebView = useIsWebView();
	// Track card click
	const handleCardClick = (item: Carousel) => {
		trackMockInterviewEvent("Job prep card clicked", {
			role_name: item.title || "",
			company_name: item.company_name || "",
			slug_id: item.slug_id || "",
			source: "Carousel",
			platform: isWebView ? "app" : "web",
		});
	};

	// Render nothing if no data
	if (data.length === 0) return null;

	const carouselLength = data.length;

	const handleNext = () => {
		setActiveIndex((prev) => (prev + 1) % carouselLength);
	};

	const handlePrev = () => {
		setActiveIndex((prev) => (prev - 1 + carouselLength) % carouselLength);
	};

	// Safely access carousel data with default values
	const currentCarousel = data[activeIndex] || ({} as Partial<Carousel>);

	const title = currentCarousel.title || "";
	const slugId = currentCarousel.slug_id || "";
	const companyLogo = currentCarousel.company_logo || "";
	const companyName = currentCarousel.company_name || "";
	const uiInfo = currentCarousel.ui_info;

	const { backgroundStyle } = renderCardContent(
		title,
		companyLogo,
		companyName,
		uiInfo
	);

	return (
		<div className="relative mx-auto w-[216px]">
			<Link
				href={`/interview-prep/mock-interview/${slugId}`}
				onClick={() => handleCardClick(currentCarousel as Carousel)}
			>
				<div
					className="relative flex h-[255px] w-[216px] flex-col justify-between
					 overflow-hidden rounded-[21px] border-[4px] border-[#F4F2F6]"
					style={{
						userSelect: "none",
						boxShadow: "0px 1.06px 10.47px 7.41px rgba(0, 0, 0, 0.05)",
						...backgroundStyle,
					}}
				>
					{/* Gradient overlay */}
					<div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/70 via-transparent to-black/70" />

					<div className="relative z-[2] flex flex-col items-center p-[16px]">
						<h1 className="m-0 text-center text-[14px] font-semibold leading-[20px] text-white">
							{title}
						</h1>
						<div className="relative z-10 mt-[2px]">
							<Image
								src={companyLogo}
								alt={companyName || title}
								width={70}
								height={27}
								priority
								className="object-contain"
							/>
						</div>
					</div>
					<div className="relative z-[2] flex h-[74px] w-full flex-col items-center rounded-b-[18px] bg-gradient-to-t from-black to-transparent py-[20px]">
						<div className="rounded-full bg-gradient-to-r from-[#0FFFBB] to-[#7905FF] px-[8px] py-[4px] text-center text-[12px] font-semibold leading-[16px] text-white">
							Practice Interview
						</div>
						<p className="mt-[4px] text-center text-[10px] font-normal leading-[12px] text-white md:mt-[8px] md:text-[20px] md:leading-[24px]">
							10 min AI Interview
						</p>
					</div>
					{uiInfo?.person_image_url && (
						<div className="absolute bottom-0 left-1/2 z-[1] h-[160px] w-[140px] -translate-x-1/2 overflow-hidden">
							<Image
								src={uiInfo.person_image_url}
								alt="Interviewer"
								width={140}
								height={160}
								priority
								className="object-cover"
							/>
						</div>
					)}
				</div>
			</Link>

			{carouselLength > 1 && (
				<>
					{/* Navigation Buttons */}
					<NavButton
						direction="prev"
						onClick={handlePrev}
						ariaLabel="Previous"
					/>
					<NavButton direction="next" onClick={handleNext} ariaLabel="Next" />

					{/* Indicators */}
					<div className="mt-4 flex justify-center space-x-2">
						{data.map((carousel, index) => {
							const key = carousel?.slug_id || `indicator-${index}`;
							return (
								<button
									type="button"
									key={key}
									onClick={() => setActiveIndex(index)}
									className={`h-2 w-2 rounded-full ${
										index === activeIndex
											? "bg-[#722ED1]"
											: "bg-gray-300"
									}`}
									aria-label={`Go to slide ${
										carousel?.title || index + 1
									}`}
								/>
							);
						})}
					</div>
				</>
			)}
		</div>
	);
};

/**
 * Component to display when no carousel data is present
 */
const EmptyState = () => (
	<div className="flex h-[255px] w-full flex-col items-center justify-center rounded-[21px] bg-gray-100 p-4">
		<div className="text-center text-gray-500">
			<p className="text-lg font-semibold">Interview preps not available</p>
			<p className="text-sm">Check back later for new interview opportunities</p>
		</div>
	</div>
);

/**
 * Main carousel component that switches between different carousel types
 * based on the number of cards available
 *
 * @param carousels - Array of carousel data objects
 */
const MIHeroCarousal: React.FC<MIHeroCarousalProps> = ({ carousels }) => {
	const [isMobileView] = useWindowResize();
	const ref = useRef<StackedCarousel>(null);
	const cardWidth = isMobileView ? 216 : 418;
	const maxVisible = 5;

	// Memoize the calculated value to prevent unnecessary recalculations
	const currentVisible = useMemo(
		() => Math.min(carousels?.length || 0, maxVisible),
		[carousels?.length]
	);

	// Only render the carousel if we have data
	if (!carousels?.length) {
		return <EmptyState />;
	}

	// For 1-2 cards, use simple carousel
	if (carousels.length < 3) {
		return <SimpleCarousel data={carousels} />;
	}

	// For 3+ cards, use stacked carousel
	return (
		<div className="relative w-full">
			<ResponsiveContainer
				carouselRef={ref}
				render={(parentWidth, carouselRef) => (
					<div>
						<StackedCarousel
							ref={carouselRef}
							slideComponent={Card}
							slideWidth={cardWidth}
							carouselWidth={parentWidth}
							data={carousels}
							currentVisibleSlide={currentVisible}
							maxVisibleSlide={maxVisible}
							useGrabCursor
						/>
						<Fab
							style={{
								position: "absolute",
								top: `calc(50% - 20px)`,
								left: 10,
								zIndex: 10,
							}}
							size="small"
							color="primary"
							onClick={() => {
								ref.current?.goBack();
							}}
						>
							<ArrowBack />
						</Fab>
						<Fab
							style={{
								position: "absolute",
								top: `calc(50% - 20px)`,
								right: 10,
								zIndex: 10,
							}}
							size="small"
							color="primary"
							onClick={() => {
								ref.current?.goNext();
							}}
						>
							<ArrowForward />
						</Fab>
					</div>
				)}
			/>
		</div>
	);
};

export default MIHeroCarousal;
